import { useEffect, useRef, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  NavLink,
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';
import './App.css';
import Home from './pages/Home';
import Leadership from './pages/Leadership';
import ProofOfConcept from './pages/ProofOfConcept';
import ConceptPage from './pages/ConceptPage';
import concepts, { CONCEPT_PATHS } from './concepts';

function smoothScrollTo(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 60;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export default function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/proof-of-concept" element={<ProofOfConcept />} />
          <Route path="/leadership" element={<Leadership />} />
          {concepts.flatMap((c) =>
            [c.path, ...c.aliases].map((p) => (
              // key on apiKey so switching briefs remounts the gate rather than
              // reusing the previous concept's unlocked state.
              <Route key={p} path={p} element={<ConceptPage key={c.apiKey} {...c} />} />
            ))
          )}
          <Route path="*" element={<Home />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const progressRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const sc = h.scrollTop || document.body.scrollTop;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      const pct = sc / max;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${pct})`;
      }
      setScrolled(sc > 12);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      const id = setTimeout(() => smoothScrollTo(location.hash), 80);
      return () => clearTimeout(id);
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <>
      <div className="scroll-progress" ref={progressRef} />
      <Header
        scrolled={scrolled}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <main id="top">{children}</main>
      <Footer />
    </>
  );
}

function Header({ scrolled, menuOpen, setMenuOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === '/';
  const proprietaryActive = CONCEPT_PATHS.includes(location.pathname);
  // A clicked dropdown link keeps focus (there is no page reload to drop it) and
  // the cursor is still over it, so :focus-within / :hover would hold the menu
  // open until the user clicks elsewhere. This forces it shut after a mouse
  // pick, and clears again once the cursor leaves the group.
  const [submenuClosed, setSubmenuClosed] = useState(false);

  function handleAnchorClick(hash) {
    return (ev) => {
      ev.preventDefault();
      setMenuOpen(false);
      if (onHome) {
        smoothScrollTo(hash);
      } else {
        navigate('/' + hash);
      }
    };
  }

  return (
    <header className={`top${scrolled ? ' scrolled' : ''}`}>
      <div className="wrap top-inner">
        <Link
          to="/"
          className="brand"
          onClick={(ev) => {
            if (onHome) {
              ev.preventDefault();
              setMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              setMenuOpen(false);
            }
          }}
        >
          <img src="/assets/rfs-logo-white-only.png" alt="Railway Flight Solutions" className="brand-logo" />
          <span className="brand-text">
            <span className="brand-name">Railway Flight Solutions</span>
            <span className="brand-suffix">LLC</span>
          </span>
        </Link>
        <nav className={`menu${menuOpen ? ' mobile-open' : ''}`}>
          <a href="/#top" onClick={handleAnchorClick('#top')}>Home</a>
          <a href="/#about" onClick={handleAnchorClick('#about')}>About</a>
          <a href="/#capabilities" onClick={handleAnchorClick('#capabilities')}>Capabilities</a>
          <NavLink
            to="/proof-of-concept"
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) => (isActive ? 'active' : undefined)}
          >
            Proof of Concept
          </NavLink>
          <NavLink
            to="/leadership"
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) => (isActive ? 'active' : undefined)}
          >
            Leadership
          </NavLink>
          <div
            className={`menu-group${proprietaryActive ? ' active' : ''}${submenuClosed ? ' closed' : ''}`}
            onMouseLeave={() => setSubmenuClosed(false)}
          >
            <button type="button" className="menu-group-trigger" aria-haspopup="true">
              Proprietary
              <svg className="caret" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className="submenu">
              {concepts.map((c) => (
                <NavLink
                  key={c.apiKey}
                  to={c.path}
                  onClick={(ev) => {
                    setMenuOpen(false);
                    // detail > 0 is a mouse/touch click; 0 is keyboard Enter, which
                    // should keep its focus.
                    if (ev.detail > 0) {
                      ev.currentTarget.blur();
                      setSubmenuClosed(true);
                    }
                  }}
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                >
                  {c.navLabel}
                </NavLink>
              ))}
            </div>
          </div>
          <a href="/#contact" className="cta" onClick={handleAnchorClick('#contact')}>
            Contact
          </a>
        </nav>
        <button
          className={`menu-toggle${menuOpen ? ' open' : ''}`}
          aria-label="Menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span /><span /><span />
        </button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div className="wrap foot-inner">
        <div className="foot-left">
          <img src="/assets/rfs-logo-white.png" alt="Railway Flight Solutions" className="brand-logo brand-logo-sm" />
          <span className="mono">© 2026 Railway Flight Solutions</span>
        </div>
        <div className="foot-right">
          <span className="item">Fort Worth, TX</span>
          <span className="item">·</span>
          <span className="item">All Rights Reserved</span>
        </div>
      </div>
    </footer>
  );
}
